import type { GenplanPlot, PlotStatusKind } from '../model/types';

export interface CurrentPlotState {
  plotId: string;
  status: PlotStatusKind;
  availability: 'available' | 'reserved' | 'sold';
  format: 'standard' | 'contractor';
  rateRub: number;
  version: number;
  formatNeedsReview: boolean;
}

let cachedPlots: readonly GenplanPlot[] | undefined;
let revision = -1;
let pendingRequest: Promise<readonly GenplanPlot[]> | undefined;

export function loadCurrentPlots(options: { force?: boolean } = {}) {
  if (pendingRequest) return pendingRequest;
  if (!options.force && cachedPlots) return Promise.resolve(cachedPlots);
  const request = Promise.all([import('../data/plots'), loadSnapshot()])
    .then(([{ genplanPlots }, snapshot]) => {
      const states = validateSnapshot(
        snapshot,
        genplanPlots.map((plot) => plot.id),
      );
      if (snapshot.revision < revision) {
        if (cachedPlots) return cachedPlots;
        throw new Error('Получена устаревшая версия генплана');
      }
      const byId = new Map(states.map((item) => [item.plotId, item]));
      cachedPlots = genplanPlots.map((plot) => {
        const state = byId.get(plot.id)!;
        return { ...plot, current: state, status: { ...plot.status, kind: state.status } };
      });
      revision = snapshot.revision;
      return cachedPlots;
    })
    .finally(() => {
      if (pendingRequest === request) pendingRequest = undefined;
    });
  pendingRequest = request;
  return request;
}

async function loadSnapshot(): Promise<{ revision: number; statuses: unknown }> {
  const response = await fetch('/api/v1/plots/statuses', {
    headers: { Accept: 'application/json' },
    cache: 'no-store',
    signal: AbortSignal.timeout(8000),
  });
  if (!response.ok) throw new Error('Не удалось обновить статусы участков');
  return response.json() as Promise<{ revision: number; statuses: unknown }>;
}

export function validateSnapshot(
  payload: { revision: number; statuses: unknown },
  ids: readonly string[],
): CurrentPlotState[] {
  if (
    !payload ||
    !Number.isSafeInteger(payload.revision) ||
    payload.revision < 1 ||
    !Array.isArray(payload.statuses) ||
    payload.statuses.length !== ids.length
  ) {
    throw new Error('Некорректный снимок генплана');
  }
  const expected = new Set(ids);
  for (const raw of payload.statuses as unknown[]) {
    if (!raw || typeof raw !== 'object') throw new Error('Некорректный участок');
    const item = raw as CurrentPlotState;
    const status =
      item.availability === 'sold' || item.availability === 'reserved'
        ? item.availability
        : item.format === 'contractor'
          ? 'price-per-sotka-with-contractor'
          : 'price-per-sotka';
    if (
      !expected.delete(item.plotId) ||
      !['available', 'reserved', 'sold'].includes(item.availability) ||
      !['standard', 'contractor'].includes(item.format) ||
      item.status !== status ||
      !Number.isSafeInteger(item.version) ||
      item.version < 1 ||
      !Number.isSafeInteger(item.rateRub) ||
      item.rateRub <= 0 ||
      typeof item.formatNeedsReview !== 'boolean'
    )
      throw new Error('Некорректные данные участка');
  }
  return payload.statuses as CurrentPlotState[];
}
